import { useRef, useCallback } from "react";
import anime from "animejs";

/**
 * Standalone scale press animation — can be called on any PIXI display object.
 * Use this when you can't use the useButtonHover hook (e.g., from a context).
 */
export const triggerScalePress = (displayObject) => {
  if (!displayObject) return;
  const origX = displayObject.scale.x;
  const origY = displayObject.scale.y;
  anime.remove(displayObject.scale);
  anime({
    targets: displayObject.scale,
    x: origX * 0.85,
    y: origY * 0.85,
    duration: 50,
    easing: "easeInQuad",
    complete: () => {
      anime({
        targets: displayObject.scale,
        x: origX,
        y: origY,
        duration: 100,
        easing: "easeOutBack",
      });
    },
  });
};

/**
 * Reusable button interaction hook for PixiJS buttons.
 * Provides:
 * - Scale hover effect on a container (1.0 → 1.08)
 * - Scale press animation on the sprite inside (squish → bounce back)
 *
 * Hover animates the container, press animates the sprite — they never conflict.
 *
 * @returns {{ hoverContainerRef, buttonRef, handlePointerOver, handlePointerOut, triggerPress }}
 */
export const useButtonHover = () => {
  const hoverContainerRef = useRef(null);
  const buttonRef = useRef(null);
  const hoverAnimRef = useRef(null);
  const pressAnimRef = useRef(null);
  const isPressAnimatingRef = useRef(false);

  const handlePointerOver = useCallback(() => {
    if (hoverContainerRef.current) {
      if (hoverAnimRef.current) hoverAnimRef.current.pause();
      hoverAnimRef.current = anime({
        targets: hoverContainerRef.current.scale,
        x: 1.08,
        y: 1.08,
        duration: 100,
        easing: "easeOutQuad",
      });
    }
  }, []);

  const handlePointerOut = useCallback(() => {
    if (hoverContainerRef.current) {
      if (hoverAnimRef.current) hoverAnimRef.current.pause();
      hoverAnimRef.current = anime({
        targets: hoverContainerRef.current.scale,
        x: 1.0,
        y: 1.0,
        duration: 100,
        easing: "easeOutQuad",
      });
    }
  }, []);

  const triggerPress = useCallback(() => {
    if (!buttonRef.current || isPressAnimatingRef.current) return;
    isPressAnimatingRef.current = true;

    // Cancel any existing press animation
    if (pressAnimRef.current) pressAnimRef.current.pause();

    // Capture the sprite's current scale (set by width/height props)
    const originalScaleX = buttonRef.current.scale.x;
    const originalScaleY = buttonRef.current.scale.y;

    // Phase 1: Quick squish down
    pressAnimRef.current = anime({
      targets: buttonRef.current.scale,
      x: originalScaleX * 0.92,
      y: originalScaleY * 0.92,
      duration: 65,
      easing: "easeInQuad",
      complete: () => {
        // Phase 2: Bounce back to original
        pressAnimRef.current = anime({
          targets: buttonRef.current.scale,
          x: originalScaleX,
          y: originalScaleY,
          duration: 120,
          easing: "easeOutBack",
          complete: () => {
            isPressAnimatingRef.current = false;
          },
        });
      },
    });
  }, []);

  return {
    hoverContainerRef,
    buttonRef,
    handlePointerOver,
    handlePointerOut,
    triggerPress,
  };
};
